// src/components/productDetail/FlashcardPreviewBlock.tsx - VERSÃO CORRIGIDA

import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './FlashcardPreviewBlock.css';

interface Card {
    _id: string;
    front: string;
    back: string;
}

interface FlashcardPreviewBlockProps {
    deckId: string;
    subdeckId: string;
    cardIds: string[];
}

export const FlashcardPreviewBlock: React.FC<FlashcardPreviewBlockProps> = ({
    deckId,
    subdeckId,
    cardIds
}) => {
    const [cards, setCards] = useState<Card[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFlipped, setIsFlipped] = useState(false);
    const [loading, setLoading] = useState(true);

    const loadCards = useCallback(async () => {
        try {
            setLoading(true);

            const response = await fetch(
                `/api/admin/marketplace/decks/${deckId}/subdecks/${subdeckId}/cards/public`
            );

            if (response.ok) {
                const data = await response.json();
                const filteredCards = data.cards.filter((card: Card) =>
                    cardIds.includes(card._id)
                );
                setCards(filteredCards);
            }
        } catch (error) {
            console.error('Erro ao carregar cards:', error);
        } finally {
            setLoading(false);
        }
    }, [deckId, subdeckId, cardIds]);

    useEffect(() => {
        loadCards();
    }, [loadCards]);

    useEffect(() => {
        setIsFlipped(false);
    }, [currentIndex]);

    const handleNext = () => {
        if (currentIndex < cards.length - 1) {
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleFlip = () => {
        setIsFlipped(!isFlipped);
    };

    const renderCardContent = (html: string) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        tempDiv.querySelectorAll('.delete-img-btn, .delete-audio-btn, .editor-only').forEach(el => el.remove());
        return { __html: tempDiv.innerHTML };
    };

    if (loading) {
        return (
            <div className="fpb-container">
                <div className="fpb-loading">
                    <div className="fpb-spinner"></div>
                    <p>Carregando preview...</p>
                </div>
            </div>
        );
    }

    if (cards.length === 0) {
        return (
            <div className="fpb-container">
                <div className="fpb-empty">
                    <p>Nenhum card selecionado para preview</p>
                </div>
            </div>
        );
    }

    const currentCard = cards[currentIndex];

    return (
        <div className="fpb-container">
            <div className="fpb-header">
                <h3>Preview dos Flashcards</h3>
                <span className="fpb-counter">
                    {currentIndex + 1} / {cards.length}
                </span>
            </div>

            <div className="fpb-card-wrapper">
                <div
                    className={`fpb-card ${isFlipped ? 'fpb-flipped' : ''}`}
                    onClick={handleFlip}
                >
                    <div className="fpb-card-side fpb-front">
                        <div
                            className="fpb-card-content"
                            dangerouslySetInnerHTML={renderCardContent(currentCard.front)}
                        />
                    </div>

                    <div className="fpb-card-side fpb-back">
                        <div
                            className="fpb-card-content"
                            dangerouslySetInnerHTML={renderCardContent(currentCard.back)}
                        />
                    </div>
                </div>
            </div>

            <div className="fpb-controls">
                <button
                    className="fpb-btn-nav"
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                >
                    <ChevronLeft size={20} />
                    Anterior
                </button>

                <button className="fpb-btn-flip" onClick={handleFlip}>
                    {isFlipped ? 'Ver Pergunta' : 'Ver Resposta'}
                </button>

                <button
                    className="fpb-btn-nav"
                    onClick={handleNext}
                    disabled={currentIndex === cards.length - 1}
                >
                    Próximo
                    <ChevronRight size={20} />
                </button>
            </div>
        </div>
    );
};